import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { ArrowRight, Search, Zap, MessageSquare, TrendingUp, Globe, ChevronRight, ChevronDown, Shield, Clock, Sparkles, BarChart3, Users, Target, Flame, Eye, Lock, Monitor, Palette, BookOpen } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { CodyFullLogo, CodyIcon } from '../components/CodyLogo';
import AnnouncementBar from '../components/AnnouncementBar';
import { useAuth } from '../lib/auth';
import analytics from '../lib/tracking';

const fadeUp = { hidden: { opacity: 0, y: 24 }, visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] } }) };
const scaleIn = { hidden: { opacity: 0, scale: 0.94 }, visible: (i = 0) => ({ opacity: 1, scale: 1, transition: { duration: 0.55, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] } }) };

/* ── Smart Mock Data Engine ── */
const NICHE_DATA = [
  { keys: ['bookkeeping','accounting','invoic','expense','tax','finance','financial','quickbooks','xero','freshbooks','payroll'],
    results: [
      { title: "Any tools for automating bookkeeping for small business?", sub: "smallbusiness", time: "2h ago", intent: "high", highlight: "Spending 4 hours a week manually entering expenses" },
      { title: "QuickBooks alternatives that don't cost a fortune?", sub: "Entrepreneur", time: "3h ago", intent: "high", highlight: "QuickBooks raised prices again and I'm done" },
      { title: "Best invoicing software for freelancers in 2026?", sub: "freelance", time: "5h ago", intent: "high", highlight: "Need automatic reminders for overdue invoices" },
      { title: "How do you track expenses when you have multiple revenue streams?", sub: "personalfinance", time: "1h ago", intent: "medium", highlight: "Spreadsheets aren't cutting it anymore" },
      { title: "Simple tax prep tool for solopreneurs?", sub: "tax", time: "4h ago", intent: "high", highlight: "Can't afford a CPA but need more than TurboTax" },
    ]},
  { keys: ['crm','sales','pipeline','lead','prospect','hubspot','salesforce','close deals','outreach','cold email'],
    results: [
      { title: "Best CRM for a bootstrapped startup with 3 salespeople?", sub: "SaaS", time: "1h ago", intent: "high", highlight: "HubSpot free tier is too limited but paid is overkill" },
      { title: "How do you manage your sales pipeline without drowning in spreadsheets?", sub: "Entrepreneur", time: "3h ago", intent: "high", highlight: "We're losing deals because nothing is tracked properly" },
      { title: "Simple CRM that actually helps close deals?", sub: "sales", time: "4h ago", intent: "high", highlight: "Tried 5 CRMs and they all feel like they're built for enterprises" },
      { title: "Looking for a CRM with good email integration", sub: "startups", time: "6h ago", intent: "medium", highlight: "Need something that works with Gmail seamlessly" },
      { title: "Alternatives to Salesforce for small teams?", sub: "smallbusiness", time: "2h ago", intent: "high", highlight: "Salesforce is way too complex for what we need" },
    ]},
  { keys: ['project management','task','team','asana','trello','jira','monday','notion','productivity','workflow','collaboration'],
    results: [
      { title: "Need a project management tool for remote team of 5", sub: "startups", time: "2h ago", intent: "high", highlight: "Something simpler than Jira but more powerful than Trello" },
      { title: "What replaced Asana for you?", sub: "productivity", time: "3h ago", intent: "high", highlight: "Asana has gotten bloated and slow" },
      { title: "Best tool for managing client projects at an agency?", sub: "digital_marketing", time: "5h ago", intent: "high", highlight: "Juggling 12 clients and everything falls through the cracks" },
      { title: "How do you keep a small dev team organized?", sub: "webdev", time: "1h ago", intent: "medium", highlight: "We've tried everything from sticky notes to enterprise tools" },
      { title: "Simple workflow tool that isn't overcomplicated?", sub: "Entrepreneur", time: "4h ago", intent: "high", highlight: "I just need tasks, deadlines, and assignments — nothing more" },
    ]},
  { keys: ['ecommerce','shop','store','sell online','shopify','woocommerce','dropship','product','retail','inventory'],
    results: [
      { title: "Best platform to sell digital products in 2026?", sub: "Entrepreneur", time: "1h ago", intent: "high", highlight: "Gumroad fees are killing my margins" },
      { title: "Shopify alternatives for small stores?", sub: "ecommerce", time: "3h ago", intent: "high", highlight: "Paying $79/month and only making $500 in sales" },
      { title: "How do you manage inventory across multiple channels?", sub: "smallbusiness", time: "5h ago", intent: "high", highlight: "Selling on Amazon, Etsy, and my own site — can't keep track" },
      { title: "Need help with abandoned cart recovery", sub: "ecommerce", time: "2h ago", intent: "medium", highlight: "70% of carts are abandoned and I'm not doing anything about it" },
      { title: "Best tools for running a subscription box business?", sub: "Entrepreneur", time: "6h ago", intent: "high", highlight: "Looking for something that handles recurring billing and shipping" },
    ]},
  { keys: ['email','newsletter','marketing','mailchimp','campaign','automation','drip','convert','subscriber'],
    results: [
      { title: "Best email marketing platform for small e-commerce?", sub: "ecommerce", time: "2h ago", intent: "high", highlight: "Mailchimp is getting too expensive for what it offers" },
      { title: "Looking for an email tool with good automation", sub: "Entrepreneur", time: "4h ago", intent: "high", highlight: "Need drip campaigns that actually feel personal" },
      { title: "How do you grow an email list from scratch?", sub: "startups", time: "1h ago", intent: "medium", highlight: "Starting at zero subscribers and need to build fast" },
      { title: "Convertkit vs Beehiiv for newsletters?", sub: "creators", time: "3h ago", intent: "high", highlight: "Switching from Substack and need better monetization" },
      { title: "What's the best tool for transactional emails?", sub: "webdev", time: "5h ago", intent: "medium", highlight: "Need reliable delivery for password resets and receipts" },
    ]},
  { keys: ['design','website','ui','ux','graphic','logo','brand','figma','canva','web design','landing page'],
    results: [
      { title: "Need a designer for SaaS landing page", sub: "startups", time: "1h ago", intent: "high", highlight: "Current page converts at less than 1%" },
      { title: "Best tool for creating social media graphics quickly?", sub: "smallbusiness", time: "3h ago", intent: "high", highlight: "Can't afford a designer but need professional-looking content" },
      { title: "Looking for a no-code website builder that doesn't look cheap", sub: "Entrepreneur", time: "2h ago", intent: "high", highlight: "Wix and Squarespace templates all look the same" },
      { title: "How much should I pay for a brand identity package?", sub: "smallbusiness", time: "5h ago", intent: "medium", highlight: "Getting quotes from $500 to $15,000 and have no idea what's fair" },
      { title: "Best UI/UX tools for a solo product designer?", sub: "design", time: "4h ago", intent: "medium", highlight: "Figma is great but looking for something more all-in-one" },
    ]},
  { keys: ['fitness','gym','workout','health','wellness','coach','training','nutrition','diet','weight','personal trainer'],
    results: [
      { title: "Best app for creating workout plans for clients?", sub: "personaltraining", time: "2h ago", intent: "high", highlight: "Managing 30+ clients on spreadsheets is a nightmare" },
      { title: "Looking for a fitness app that tracks nutrition AND workouts", sub: "fitness", time: "1h ago", intent: "high", highlight: "MyFitnessPal is great for food but terrible for programming" },
      { title: "How do online fitness coaches manage their business?", sub: "Entrepreneur", time: "4h ago", intent: "high", highlight: "Need something for scheduling, payments, and program delivery" },
      { title: "Best platform for selling online fitness programs?", sub: "fitness", time: "3h ago", intent: "medium", highlight: "Want to create a course but don't know where to host it" },
      { title: "Recommendations for a meal planning tool?", sub: "nutrition", time: "5h ago", intent: "high", highlight: "Need to build meal plans for clients with different dietary needs" },
    ]},
  { keys: ['ai','artificial intelligence','chatbot','gpt','machine learning','automat','llm','openai','claude','copilot'],
    results: [
      { title: "Best AI tool for automating customer support?", sub: "SaaS", time: "1h ago", intent: "high", highlight: "Getting 200+ tickets a day and can't hire fast enough" },
      { title: "Looking for AI writing tool that doesn't sound like AI", sub: "Entrepreneur", time: "2h ago", intent: "high", highlight: "ChatGPT output is too generic for our brand voice" },
      { title: "AI tools for data analysis that non-technical people can use?", sub: "startups", time: "3h ago", intent: "high", highlight: "Our team doesn't know SQL but needs insights from our data" },
      { title: "Has anyone used AI for lead qualification?", sub: "sales", time: "4h ago", intent: "medium", highlight: "Spending too much time qualifying leads that never convert" },
      { title: "Best AI coding assistant in 2026?", sub: "webdev", time: "5h ago", intent: "medium", highlight: "GitHub Copilot is good but looking for something better" },
    ]},
  { keys: ['agency','seo','marketing agency','social media','ppc','ads','advertising','google ads','facebook ads','content'],
    results: [
      { title: "Which agencies do you recommend for SEO in 2026?", sub: "smallbusiness", time: "2h ago", intent: "high", highlight: "Need help ranking our local business in Google" },
      { title: "Looking for a marketing agency that understands SaaS", sub: "SaaS", time: "3h ago", intent: "high", highlight: "Previous agency didn't understand our funnel at all" },
      { title: "Best way to find clients for my marketing agency?", sub: "Entrepreneur", time: "1h ago", intent: "high", highlight: "Just started my agency and need my first 5 clients" },
      { title: "How much should I budget for PPC management?", sub: "smallbusiness", time: "5h ago", intent: "medium", highlight: "Spending $3k/month on ads with no idea if it's working" },
      { title: "Anyone hired a freelance social media manager? Worth it?", sub: "smallbusiness", time: "4h ago", intent: "high", highlight: "Don't have time to post but know we're missing out" },
    ]},
  { keys: ['course','teach','education','learn','tutoring','coaching','mentor','online course','training program','lms'],
    results: [
      { title: "Best platform for selling online courses in 2026?", sub: "Entrepreneur", time: "2h ago", intent: "high", highlight: "Teachable is getting expensive and their UI feels dated" },
      { title: "How do you price a coaching program?", sub: "Entrepreneur", time: "1h ago", intent: "medium", highlight: "Have expertise but no idea what to charge" },
      { title: "Looking for a platform to host live workshops", sub: "creators", time: "3h ago", intent: "high", highlight: "Zoom is too basic, need something with payments built in" },
      { title: "Best LMS for corporate training?", sub: "EdTech", time: "4h ago", intent: "high", highlight: "Need to train 500 employees across 3 locations" },
      { title: "How do you create a community around your course?", sub: "Entrepreneur", time: "5h ago", intent: "medium", highlight: "Students finish the course and disappear" },
    ]},
];

const FALLBACK_RESULTS = [
  { title: "Anyone know a good tool for {{query}}?", sub: "Entrepreneur", time: "2h ago", intent: "high", highlight: "Looking for something that actually works and isn't overpriced" },
  { title: "What's the best solution for {{query}} in 2026?", sub: "startups", time: "1h ago", intent: "high", highlight: "Tried a few options but nothing really clicked" },
  { title: "Recommendations for {{query}}?", sub: "smallbusiness", time: "4h ago", intent: "high", highlight: "Need something simple that saves me time" },
  { title: "How are you handling {{query}} for your business?", sub: "SaaS", time: "3h ago", intent: "medium", highlight: "Current process is completely manual" },
  { title: "Frustrated with the options for {{query}}. What am I missing?", sub: "Entrepreneur", time: "5h ago", intent: "high", highlight: "There has to be a better way to do this" },
];

function getResults(query) {
  const q = query.toLowerCase().trim();
  const words = q.split(/\s+/);

  // Score each niche by keyword matches
  let bestMatch = null;
  let bestScore = 0;
  for (const niche of NICHE_DATA) {
    let score = 0;
    for (const key of niche.keys) {
      if (q.includes(key)) score += key.length; // longer matches = more specific = higher score
      for (const word of words) {
        if (word.length > 2 && key.includes(word)) score += 1;
      }
    }
    if (score > bestScore) { bestScore = score; bestMatch = niche; }
  }

  if (bestMatch && bestScore > 2) {
    // Add dynamic reply with user's product
    return bestMatch.results.map(r => ({
      ...r,
      reply: generateReply(q, r),
    }));
  }

  // Fallback — dynamically create results using the query
  return FALLBACK_RESULTS.map(r => ({
    ...r,
    title: r.title.replace('{{query}}', q),
    highlight: r.highlight,
    reply: generateReply(q, r),
  }));
}

function generateReply(query, result) {
  const replies = [
    `This is exactly the kind of problem that ${query} solutions are built for. I'd recommend looking into purpose-built tools — they tend to save a ton of time compared to generic alternatives.`,
    `I've been in this exact situation. What worked for me was finding a ${query} solution that focused on simplicity over features. Happy to share more if you're interested.`,
    `Great question. For ${query}, the key is finding something that integrates with your existing workflow. There are some newer tools specifically designed for this.`,
    `I actually help people with ${query} — the biggest mistake I see is overcomplicating it. Start simple, see results, then scale.`,
    `Have you looked into newer ${query} tools? The space has changed a lot recently. There are options now that didn't exist 6 months ago.`,
  ];
  return replies[Math.abs(hashCode(result.title)) % replies.length];
}

function hashCode(s) { let h = 0; for (let i = 0; i < s.length; i++) h = ((h << 5) - h) + s.charCodeAt(i); return h; }

/* ── Result Card ── */
function ResultCard({ result, index, gated = false }) {
  const intentColor = result.intent === 'high' ? { bg: '#FFE8DE', fg: '#9A3412', icon: '🔥', label: 'High Intent' } : { bg: '#FFF7E6', fg: '#92400E', icon: '🟡', label: 'Medium Intent' };
  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 + index * 0.15, duration: 0.4 }}>
      <Card className={`p-5 rounded-2xl border-[#E6E8EE] bg-white hover:shadow-lg transition-shadow relative overflow-hidden ${gated ? 'select-none' : ''}`}>
        {gated && <div className="absolute inset-0 bg-white/60 backdrop-blur-[3px] z-10 flex items-center justify-center"><Lock className="w-5 h-5 text-[#9ca3af]" /></div>}
        <div className="flex items-start justify-between gap-3 mb-3">
          <h3 className="text-[15px] font-semibold text-[#0B0F17] leading-snug">{result.title}</h3>
          <span className="flex-shrink-0 text-xs font-bold px-2.5 py-1 rounded-full whitespace-nowrap" style={{ background: intentColor.bg, color: intentColor.fg }}>
            {intentColor.icon} {intentColor.label}
          </span>
        </div>
        <p className="text-xs text-[#5B667A] mb-3">r/{result.sub} • Posted {result.time}</p>
        <div className="mb-3 p-3 rounded-lg bg-[#FFFBF5] border border-[#FFE8DE]/60">
          <p className="text-[10px] font-semibold text-[#D97706] uppercase tracking-wider mb-1">Why this matters:</p>
          <p className="text-sm text-[#0B0F17] italic">"{result.highlight}"</p>
        </div>
        <div className="p-3 rounded-lg bg-[#F6F7F9] border border-[#E6E8EE]/60">
          <p className="text-[10px] font-semibold text-[#5B667A] uppercase tracking-wider mb-1">Suggested reply:</p>
          <p className="text-xs text-[#374151] leading-relaxed">"{result.reply}"</p>
        </div>
      </Card>
    </motion.div>
  );
}

/* ── FAQ Item ── */
function FAQ({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-[#E6E8EE] last:border-b-0">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between py-5 text-left">
        <span className="text-[15px] font-semibold text-[#0B0F17] pr-4">{q}</span>
        <ChevronDown className={`w-5 h-5 text-[#5B667A] flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      <AnimatePresence>{open && (
        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }}>
          <p className="text-sm text-[#5B667A] leading-relaxed pb-5">{a}</p>
        </motion.div>
      )}</AnimatePresence>
    </div>
  );
}

/* ── Live Example Cards ── */
const LIVE_EXAMPLES = [
  { title: "Any tools for automating invoicing for freelancers?", sub: "freelance", time: "2h ago", intent: "high", highlight: "Spending 3 hours a week on invoices", reply: "I built something exactly for this. It auto-generates invoices from your time tracking." },
  { title: "Best email marketing platform for small e-commerce?", sub: "ecommerce", time: "4h ago", intent: "high", highlight: "Mailchimp is getting too expensive", reply: "We switched from Mailchimp last year and cut costs by 60%. Happy to share what we use." },
  { title: "How do you handle customer support at scale?", sub: "SaaS", time: "1h ago", intent: "medium", highlight: "Growing too fast for email-based support", reply: "We hit the same wall at 500 users. Moved to a ticket system and response times dropped 70%." },
  { title: "Looking for a simple analytics tool that isn't GA", sub: "webdev", time: "3h ago", intent: "high", highlight: "Google Analytics is overkill for my use case", reply: "There are some great lightweight alternatives now. Privacy-focused too, which users love." },
  { title: "Need help with social media management for my restaurant", sub: "smallbusiness", time: "6h ago", intent: "medium", highlight: "Don't have time to post consistently", reply: "Scheduling tools can be a lifesaver. Set aside 1 hour/week and batch your content." },
  { title: "What's the best way to collect product feedback?", sub: "ProductManagement", time: "5h ago", intent: "high", highlight: "Users churn before we even know what's wrong", reply: "In-app feedback widgets changed everything for us. You catch issues before they become cancellations." },
];

/* ═══════════════ LANDING ═══════════════ */
export default function Landing() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const resultsRef = useRef(null);

  useEffect(() => { const h = () => setScrolled(window.scrollY > 20); window.addEventListener('scroll', h, { passive: true }); return () => window.removeEventListener('scroll', h); }, []);

  const handleDemo = (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    setLoading(true);
    setResults(null);
    analytics.custom('DemoStarted', { query });
    setTimeout(() => {
      setResults(getResults(query));
      setLoading(false);
      setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    }, 1500);
  };

  const handleSignupFromDemo = () => {
    analytics.custom('DemoSignup', { query });
    navigate('/signup');
  };

  return (
    <div className="min-h-screen bg-white overflow-hidden">
      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'shadow-[0_1px_3px_rgba(0,0,0,0.06)]' : ''}`}>
        <AnnouncementBar />
        <div className={`transition-all duration-300 ${scrolled ? 'bg-white/80 backdrop-blur-2xl border-b border-[#E6E8EE]/60' : 'bg-white/60 backdrop-blur-sm'}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-[64px] flex items-center justify-between">
            <CodyFullLogo height={36} />
            <nav className="hidden md:flex items-center gap-1 bg-[#F6F7F9]/70 backdrop-blur-sm rounded-full px-1.5 py-1 border border-[#E6E8EE]/50">
              {['How It Works', 'Examples', 'Pricing', 'FAQ'].map(item => (
                <button key={item} onClick={() => document.getElementById(item.toLowerCase().replace(/\s+/g, '-'))?.scrollIntoView({ behavior: 'smooth' })}
                  className="px-4 py-1.5 rounded-full text-[13px] font-medium text-[#5B667A] hover:text-[#0B0F17] hover:bg-white/80 transition-all">{item}</button>
              ))}
            </nav>
            <div className="flex items-center gap-3">
              {user ? (
                <Button onClick={() => navigate('/dashboard')} className="h-9 px-5 rounded-full text-[13px] font-semibold cta-btn shadow-lg" style={{ background: 'linear-gradient(135deg, #FF6A2A 0%, #FF4500 100%)' }}>Dashboard</Button>
              ) : (
                <>
                  <Link to="/login" className="text-[13px] text-[#5B667A] hover:text-[#0B0F17] font-medium hidden sm:block">Log in</Link>
                  <Button onClick={() => navigate('/signup')} className="h-9 px-5 rounded-full text-[13px] font-semibold cta-btn shadow-lg shadow-[#FF4500]/20" style={{ background: 'linear-gradient(135deg, #FF6A2A 0%, #FF4500 100%)' }}>
                    Find leads
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* ── HERO + INTERACTIVE DEMO ── */}
      <section className="relative pt-36 sm:pt-40 pb-16 bg-[#FAFBFC]">
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <svg className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg"><defs><pattern id="hd" x="0" y="0" width="32" height="32" patternUnits="userSpaceOnUse"><circle cx="1" cy="1" r="1" fill="rgba(0,0,0,0.05)" /></pattern><radialGradient id="hf" cx="50%" cy="30%" r="60%"><stop offset="0%" stopColor="white" stopOpacity="0" /><stop offset="100%" stopColor="white" stopOpacity="1" /></radialGradient></defs><rect width="100%" height="100%" fill="url(#hd)" /><rect width="100%" height="100%" fill="url(#hf)" /></svg>
        </div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div variants={fadeUp} initial="hidden" animate="visible" className="text-center mb-10">
            <motion.h1 variants={fadeUp} custom={0} className="text-[2.5rem] sm:text-[3.25rem] lg:text-[3.75rem] font-extrabold tracking-[-0.03em] leading-[1.08] mb-5" style={{ fontFamily: "'Space Grotesk'" }}>
              <span className="gradient-text">117+ people</span> are asking for your product on Reddit right now
            </motion.h1>
            <motion.p variants={fadeUp} custom={1} className="text-lg text-[#5B667A] mb-8 max-w-2xl mx-auto leading-relaxed">
              We scan Reddit 24/7 and show you real posts from buyers actively looking for solutions like yours — so you can close them before your competitors do.
            </motion.p>

            {/* Interactive Demo Input */}
            <motion.form variants={fadeUp} custom={2} onSubmit={handleDemo} className="max-w-xl mx-auto">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex-1 relative group">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9ca3af] group-focus-within:text-[#FF4500] transition-colors" />
                  <Input type="text" value={query} onChange={(e) => setQuery(e.target.value)}
                    placeholder="What do you sell? (e.g. AI bookkeeping tool)"
                    className="pl-12 h-[56px] rounded-2xl border-[#E6E8EE] text-base shadow-[0_2px_8px_rgba(0,0,0,0.04)] focus:shadow-[0_4px_16px_rgba(255,69,0,0.1)] focus:border-[#FF4500]/30 transition-all bg-white w-full"
                    data-testid="landing-demo-input" />
                </div>
                <Button type="submit" disabled={loading || !query.trim()}
                  className="w-full sm:w-auto h-[56px] px-8 rounded-2xl text-[15px] font-bold shadow-xl shadow-[#FF4500]/20 cta-btn"
                  style={{ background: 'linear-gradient(135deg, #FF6A2A 0%, #FF4500 100%)' }}
                  data-testid="landing-demo-button">
                  {loading ? 'Scanning Reddit…' : 'Show me my buyers'} {!loading && <ArrowRight className="ml-2 w-5 h-5" />}
                </Button>
              </div>
              <p className="text-xs text-[#9ca3af] mt-3">Takes 10 seconds • No signup required to preview</p>
            </motion.form>
          </motion.div>

          {/* Demo Results */}
          <div ref={resultsRef}>
            <AnimatePresence>
              {loading && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="text-center py-12">
                  <div className="w-12 h-12 rounded-full border-4 border-[#E6E8EE] border-t-[#FF4500] animate-spin mx-auto mb-4" />
                  <p className="text-sm font-medium text-[#5B667A]">Scanning Reddit for high-intent posts…</p>
                </motion.div>
              )}

              {results && !loading && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                  <p className="text-sm font-semibold text-[#5B667A] mb-4">Here are people already looking for what you offer:</p>
                  <div className="space-y-4">
                    {results.slice(0, showAll ? results.length : 2).map((r, i) => (
                      <ResultCard key={i} result={r} index={i} />
                    ))}
                    {!showAll && results.length > 2 && (
                      <ResultCard result={results[2]} index={2} gated={true} />
                    )}
                  </div>

                  {!showAll && (
                    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8 }}
                      className="mt-6 p-6 rounded-2xl bg-gradient-to-r from-[#FFF8F5] to-[#FFF2EA] border border-[#FF4500]/10 text-center">
                      <Lock className="w-8 h-8 text-[#FF4500] mx-auto mb-3" />
                      <h3 className="text-lg font-bold text-[#0B0F17] mb-1" style={{ fontFamily: "'Space Grotesk'" }}>Want to see more leads like this?</h3>
                      <p className="text-sm text-[#5B667A] mb-4">Create a free account in 10 seconds</p>
                      <Button onClick={handleSignupFromDemo} className="h-12 px-8 rounded-xl text-sm font-bold cta-btn shadow-lg shadow-[#FF4500]/20" style={{ background: 'linear-gradient(135deg, #FF6A2A 0%, #FF4500 100%)' }}>
                        Unlock all results <ArrowRight className="ml-2 w-4 h-4" />
                      </Button>
                    </motion.div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </section>

      {/* ── TRUSTED BY ── */}
      <section className="py-10 bg-white border-b border-[#E6E8EE]/50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="flex flex-col items-center">
            <p className="text-xs text-[#9ca3af] font-medium uppercase tracking-wider mb-8">Trusted by growth teams at</p>
            <div className="flex flex-wrap items-center justify-center gap-x-14 gap-y-6">
              {[
                { src: '/logo-inkfree.png', alt: 'InkFree', cls: 'h-[49px]' },
                { src: '/logo-iykyk.png', alt: 'IYKYK', cls: 'h-[50px]' },
                { src: '/logo-vidspark.png', alt: 'VidSpark', cls: 'h-[50px]' },
                { src: '/logo-zest.png', alt: 'Zest', cls: 'h-[58px]' },
              ].map(logo => (
                <div key={logo.alt} className={`${logo.cls} flex items-center justify-center`}>
                  <img src={logo.src} alt={logo.alt} className="h-full w-auto object-contain brightness-0 opacity-40 hover:opacity-60 transition-opacity" />
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── FOUNDER CREDIBILITY ── */}
      <section className="py-12 bg-[#FAFBFC]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="flex flex-col sm:flex-row items-center gap-6 sm:gap-8">
            <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full overflow-hidden flex-shrink-0 shadow-lg shadow-[#FF4500]/20 ring-4 ring-white">
              <img src="/ryan-thompson.jpg" alt="Ryan Thompson" className="w-full h-full object-cover" />
            </div>
            <div className="text-center sm:text-left">
              <p className="text-[15px] text-[#374151] leading-relaxed italic mb-3">"I built Cody because I was tired of manually searching Reddit for potential customers. Now we surface high-intent conversations automatically — so founders can focus on closing, not searching."</p>
              <div>
                <p className="text-sm font-bold text-[#0B0F17]">Ryan Thompson</p>
                <p className="text-xs text-[#5B667A]">Founder & CEO, Cody • Serial Entrepreneur • 20+ years in growth marketing</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── PROOF STRIP ── */}
      <section className="py-10 bg-white border-y border-[#E6E8EE]/50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-3 gap-8 text-center">
            {[
              { value: '1,842+', label: 'buyer-intent posts found this week' },
              { value: '300+', label: 'founders & marketers' },
              { value: '3–7', label: 'new leads per day on average' },
            ].map((s, i) => (
              <motion.div key={i} variants={fadeUp} initial="hidden" whileInView="visible" custom={i} viewport={{ once: true }}>
                <p className="text-2xl sm:text-3xl font-extrabold text-[#0B0F17]" style={{ fontFamily: "'Space Grotesk'" }}>{s.value}</p>
                <p className="text-xs sm:text-sm text-[#5B667A] mt-1">{s.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section className="py-20 bg-[#FAFBFC]" id="how-it-works">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-3" style={{ fontFamily: "'Space Grotesk'" }}>Turn Reddit into your highest-converting growth channel</h2>
          </motion.div>
          <div className="grid sm:grid-cols-3 gap-8">
            {[
              { step: '1', icon: Search, title: 'We find buyers for you', desc: 'We scan Reddit for posts where people are actively asking for solutions like yours.', color: '#FF4500' },
              { step: '2', icon: Eye, title: 'We highlight real buying intent', desc: 'No noise. Only posts where people are ready to buy or seriously evaluating options.', color: '#2563EB' },
              { step: '3', icon: Target, title: 'You reply and close', desc: 'Jump into conversations early and win customers before competitors even see them.', color: '#16A34A' },
            ].map((item, i) => (
              <motion.div key={i} variants={fadeUp} initial="hidden" whileInView="visible" custom={i} viewport={{ once: true }}>
                <Card className="p-7 rounded-2xl border-[#E6E8EE] bg-white h-full text-center feature-card">
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-5 shadow-sm" style={{ background: `${item.color}12` }}>
                    <item.icon className="w-6 h-6" style={{ color: item.color }} />
                  </div>
                  <h3 className="text-lg font-bold mb-2" style={{ fontFamily: "'Space Grotesk'" }}>{item.title}</h3>
                  <p className="text-sm text-[#5B667A]">{item.desc}</p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── LIVE EXAMPLES ── */}
      <section className="py-20 bg-white" id="examples">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-4">
            <Badge className="text-xs font-semibold bg-red-50 text-red-600 border-0 mb-4">🔴 Live Buyer Intent</Badge>
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-3" style={{ fontFamily: "'Space Grotesk'" }}>Real posts. Real buyers. Real opportunities.</h2>
            <p className="text-[#5B667A]">These are actual conversations happening right now on Reddit.</p>
          </motion.div>
          <div className="grid md:grid-cols-2 gap-4 mt-10">
            {LIVE_EXAMPLES.map((ex, i) => (
              <motion.div key={i} variants={scaleIn} initial="hidden" whileInView="visible" custom={i} viewport={{ once: true, margin: "-50px" }}>
                <ResultCard result={ex} index={0} />
              </motion.div>
            ))}
          </div>
          <p className="text-xs text-[#9ca3af] text-center mt-6">New posts are added daily across thousands of subreddits</p>
        </div>
      </section>

      {/* ── USE CASES ── */}
      <section className="py-20 bg-[#FAFBFC]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight" style={{ fontFamily: "'Space Grotesk'" }}>Built for people who need customers — now</h2>
          </motion.div>
          <div className="grid sm:grid-cols-3 gap-6">
            {[
              { title: 'SaaS Founders', desc: 'Find users already asking for tools like yours before your competitors do.', icon: Monitor, color: '#FF4500' },
              { title: 'Agencies', desc: 'Discover businesses actively looking for services you offer.', icon: Users, color: '#2563EB' },
              { title: 'Info Products / Creators', desc: 'Tap into conversations where your expertise is already in demand.', icon: BookOpen, color: '#16A34A' },
            ].map((item, i) => (
              <motion.div key={i} variants={scaleIn} initial="hidden" whileInView="visible" custom={i} viewport={{ once: true }}>
                <Card className="feature-card p-7 rounded-2xl border-[#E6E8EE] h-full bg-white">
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-5" style={{ background: `${item.color}10` }}>
                    <item.icon className="w-5 h-5" style={{ color: item.color }} />
                  </div>
                  <h3 className="text-base font-bold mb-2" style={{ fontFamily: "'Space Grotesk'" }}>{item.title}</h3>
                  <p className="text-sm text-[#5B667A] leading-relaxed">{item.desc}</p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PRICING ── */}
      <PricingSection navigate={navigate} />

      {/* ── CTA ── */}
      <section className="py-24 bg-[#0B0F17] relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <motion.div className="absolute w-[500px] h-[500px] rounded-full" style={{ background: 'radial-gradient(circle, rgba(255,69,0,0.1) 0%, transparent 70%)', top: '-20%', left: '10%' }} animate={{ x: [0, 30, 0] }} transition={{ duration: 15, repeat: Infinity }} />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold mb-5 text-white" style={{ fontFamily: "'Space Grotesk'" }}>
              You're missing customers already asking for your product
            </h2>
            <p className="text-lg text-[#8B95A9] mb-8 max-w-xl mx-auto">Cody helps you find them before anyone else does.</p>
            <Button onClick={() => navigate('/signup')} className="h-14 px-10 rounded-2xl text-base font-bold cta-btn shadow-xl shadow-[#FF4500]/30" style={{ background: 'linear-gradient(135deg, #FF6A2A 0%, #FF4500 100%)' }}>
              Start free <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <p className="text-xs text-[#5B667A] mt-4">No credit card required • Takes 10 seconds</p>
          </motion.div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="py-20 bg-white" id="faq">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-12">
            <h2 className="text-3xl font-extrabold" style={{ fontFamily: "'Space Grotesk'" }}>Frequently asked questions</h2>
          </motion.div>
          <FAQ q="Is this scraping or spammy?" a="No. Cody surfaces publicly available Reddit posts where users are already asking for help. You engage naturally — no spam, no automation required." />
          <FAQ q="How fresh is the data?" a="Posts are pulled in near real-time. You'll see conversations happening within hours, not days." />
          <FAQ q="Will this get me banned from Reddit?" a="No. Cody doesn't post for you. You choose how and when to engage — just like any normal Reddit user." />
          <FAQ q="What kind of results can I expect?" a="Most users find 3–7 relevant opportunities per day depending on their niche." />
          <FAQ q="Do I need technical skills?" a="Not at all. Just type what you sell and we'll do the rest." />
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#E6E8EE] bg-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2"><CodyIcon size={28} /><span className="text-sm font-semibold" style={{ fontFamily: "'Space Grotesk'" }}>Cody</span></div>
          <div className="flex gap-6 text-xs text-[#9ca3af]"><a href="/terms" className="hover:text-[#5B667A] transition-colors">Terms</a><a href="/privacy" className="hover:text-[#5B667A] transition-colors">Privacy</a><span className="hover:text-[#5B667A] cursor-pointer transition-colors">Contact</span></div>
        </div>
      </footer>

      {/* ── MOBILE STICKY CTA ── */}
      {scrolled && !results && (
        <div className="fixed bottom-0 left-0 right-0 sm:hidden z-50 p-3 bg-white/90 backdrop-blur-xl border-t border-[#E6E8EE]">
          <Button onClick={() => document.querySelector('[data-testid="landing-demo-input"]')?.focus()}
            className="w-full h-12 rounded-xl text-sm font-bold cta-btn shadow-lg" style={{ background: 'linear-gradient(135deg, #FF6A2A 0%, #FF4500 100%)' }}>
            Find customers now <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
        </div>
      )}
    </div>
  );
}

/* ── Pricing ── */
function PricingSection({ navigate }) {
  const [annual, setAnnual] = useState(false);
  const tiers = [
    { name: 'Startup', desc: 'Get started for free', monthly: 0, yearly: 0, cta: 'Start free', popular: false,
      features: [[true,'1 brand'],[true,'5 keywords'],[true,'5 subreddits'],[true,'3 leads / day'],[true,'Intent scoring'],[true,'Daily scan refresh'],[false,'AI reply generator'],[false,'Slack / email alerts']], extra: null },
    { name: 'Growth', desc: 'For founders & marketers', monthly: 49, yearly: 490, cta: 'Start Growth Plan', popular: true,
      features: [[true,'2 brands included'],[true,'50 keywords / brand'],[true,'50 subreddits / brand'],[true,'Unlimited leads'],[true,'Intent scoring'],[true,'Real-time scanning'],[true,'AI reply generator (150/mo)'],[true,'Slack / email alerts']], extra: { text: 'Add brands for $29 USD/mo each' } },
    { name: 'Agency', desc: 'For teams & agencies', monthly: 99, yearly: 990, cta: 'Start Agency Plan', popular: false,
      features: [[true,'10 brands included'],[true,'50 keywords / brand'],[true,'50 subreddits / brand'],[true,'Unlimited leads'],[true,'Intent scoring'],[true,'Real-time scanning'],[true,'AI reply generator (500/mo)'],[true,'Slack / email alerts']], extra: { text: 'Add brands for $19 USD/mo each' } },
  ];
  return (
    <section className="py-24 bg-white" id="pricing">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <h2 className="text-3xl sm:text-[2.75rem] font-extrabold mb-4 tracking-tight" style={{ fontFamily: "'Space Grotesk'" }}>Start small. Close your first customers fast.</h2>
          <p className="text-[#5B667A] text-base mb-8">Cancel anytime • No contracts • Takes 2 minutes to start</p>
          <div className="inline-flex items-center gap-3 bg-[#F6F7F9] rounded-full p-1 border border-[#E6E8EE]">
            <button onClick={() => setAnnual(false)} className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${!annual ? 'bg-white text-[#0B0F17] shadow-sm' : 'text-[#5B667A]'}`}>Monthly</button>
            <button onClick={() => setAnnual(true)} className={`px-5 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${annual ? 'bg-white text-[#0B0F17] shadow-sm' : 'text-[#5B667A]'}`}>Annual <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#E9F9EF] text-[#16A34A]">Save 17%</span></button>
          </div>
        </div>
        <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {tiers.map((tier, i) => {
            const price = annual ? tier.yearly : tier.monthly;
            const period = annual ? '/year' : '/month';
            return (
              <Card key={tier.name} className={`relative rounded-2xl overflow-hidden h-full flex flex-col ${tier.popular ? 'border-[#FF4500] border-2 shadow-[0_4px_24px_rgba(255,69,0,0.12)]' : 'border-[#E6E8EE]'}`}>
                {tier.popular && <div className="bg-gradient-to-r from-[#FF4500] to-[#FF6A2A] text-center py-1.5"><span className="text-[11px] font-bold text-white uppercase tracking-wider">Most Popular</span></div>}
                <div className={`p-7 flex-1 flex flex-col ${!tier.popular ? 'pt-[calc(1.75rem+30px)]' : ''}`}>
                  <h3 className="text-lg font-bold mb-1" style={{ fontFamily: "'Space Grotesk'" }}>{tier.name}</h3>
                  <p className="text-xs text-[#5B667A] mb-5">{tier.desc}</p>
                  <div className="flex items-end gap-1 mb-6">{price === 0 ? <span className="text-4xl font-extrabold" style={{ fontFamily: "'Space Grotesk'" }}>Free</span> : <><span className="text-4xl font-extrabold" style={{ fontFamily: "'Space Grotesk'" }}>${price}</span><span className="text-[#5B667A] text-sm font-medium mb-1"> USD{period}</span></>}</div>
                  <div className="space-y-3 mb-6 flex-1">{tier.features.map(([has, text], fi) => (
                    <div key={fi} className="flex items-center gap-2.5"><div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${has ? 'bg-[#E9F9EF]' : 'bg-[#F3F4F6]'}`}>{has ? <svg className="w-3 h-3 text-[#16A34A]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg> : <svg className="w-3 h-3 text-[#9ca3af]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" /></svg>}</div><span className={`text-sm ${has ? 'text-[#0B0F17]' : 'text-[#9ca3af]'}`}>{text}</span></div>
                  ))}</div>
                  {tier.extra && <div className="p-3 rounded-lg bg-[#FAFBFC] border border-[#E6E8EE] mb-5 text-center"><span className="text-xs text-[#5B667A]">{tier.extra.text}</span></div>}
                  <Button onClick={() => navigate('/signup')} className={`w-full h-11 rounded-xl text-sm font-bold ${tier.popular ? 'cta-btn shadow-lg shadow-[#FF4500]/20' : ''}`}
                    variant={tier.popular ? 'default' : 'outline'} style={tier.popular ? { background: 'linear-gradient(135deg, #FF6A2A 0%, #FF4500 100%)' } : {}}>{tier.cta}</Button>
                </div>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
}
